// Reine Entscheidungslogik für das HITL-Approval-Center (F-1).
//
// Bewusst frei von React: damit die Logik gegen einen autoritativen Stand
// getestet werden kann statt gegen einen veralteten Render-Snapshot. In
// ChatView werden die Entscheidungen in einem Ref akkumuliert und über diese
// Funktionen ausgewertet, sodass zwei schnelle Klicks nicht beide denselben
// Stale-Snapshot sehen (kein Doppel-Resume, kein hängengebliebener Run).

import type { ApprovalView } from "../api";

export type ApprovalDecision = "approved" | "rejected";

/** Eine einzelne Entscheidung immutabel in die Entscheidungs-Map mergen. */
export function mergeDecision(
  decisions: Readonly<Record<string, ApprovalDecision>>,
  approvalId: string,
  decision: ApprovalDecision,
): Record<string, ApprovalDecision> {
  return { ...decisions, [approvalId]: decision };
}

/**
 * True, sobald JEDE Approval des Turns entschieden ist. Eine leere Liste gilt
 * nie als entschieden, damit kein Run ohne tatsächliche Freigabe fortgesetzt
 * wird.
 */
export function allApprovalsDecided(
  pending: readonly { id: string }[],
  decisions: Readonly<Record<string, ApprovalDecision>>,
): boolean {
  return pending.length > 0 && pending.every((a) => Boolean(decisions[a.id]));
}

export interface ApprovalRunGroup {
  runId: string;
  approvals: ApprovalView[];
}

/**
 * Gruppiert Pending-Approvals nach `runId`. Gruppen werden nach der ältesten
 * `createdAt` je Run sortiert (stabil); Approvals innerhalb einer Gruppe
 * behalten ihre Eingangsreihenfolge. Rein — keine Seiteneffekte.
 */
export function groupApprovalsByRun(
  approvals: readonly ApprovalView[],
): ApprovalRunGroup[] {
  const byRun = new Map<string, ApprovalView[]>();
  for (const a of approvals) {
    const list = byRun.get(a.runId);
    if (list) list.push(a);
    else byRun.set(a.runId, [a]);
  }
  const oldest = (list: ApprovalView[]): string =>
    list.reduce((min, a) => (a.createdAt < min ? a.createdAt : min), list[0]?.createdAt ?? "");
  return [...byRun.entries()]
    .map(([runId, list]) => ({ runId, approvals: list }))
    .sort((l, r) => {
      const lo = oldest(l.approvals);
      const ro = oldest(r.approvals);
      return lo < ro ? -1 : lo > ro ? 1 : 0;
    });
}
